import React from "react";
import { Box } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { BUTTON_RESET, C, FONT_BODY, FOCUS_RING, RADIUS, SHADOW } from "../../../utils/designTokens";

// Each half is 36px drawn, with its hit area stretched to 44px.
const stepButton = {
  ...BUTTON_RESET,
  position: "relative",
  width: 36,
  height: 36,
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: C.ink,
  transition: "background-color .15s ease",
  "&:hover": { bgcolor: C.surface2 },
  "&::before": { content: '""', position: "absolute", inset: -4 },
  "&:disabled": { cursor: "default", color: C.muted, bgcolor: "transparent" },
  ...FOCUS_RING,
};

/**
 * −/+ stepper. White and floating on a photo (floating), or flat inside a
 * list row. The count is announced politely as it changes. redAdd draws the +
 * as a filled red button (checkout's order summary).
 */
function QtyStepper({ quantity, onAdd, onRemove, itemName = "item", floating = false, disabled = false, redAdd = false }) {
  return (
    <Box
      role="group"
      aria-label={`${itemName} quantity`}
      onClick={(e) => e.stopPropagation()}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        height: 40,
        p: "2px",
        boxSizing: "border-box",
        borderRadius: RADIUS.pill,
        bgcolor: C.bg,
        border: `1px solid ${C.borderStrong}`,
        boxShadow: floating ? SHADOW.button : "none",
        flexShrink: 0,
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={onRemove}
        disabled={disabled}
        aria-label={quantity <= 1 ? `Remove ${itemName}` : `Remove one ${itemName}`}
        sx={stepButton}
      >
        <RemoveRoundedIcon sx={{ fontSize: 18 }} />
      </Box>
      <Box
        component="span"
        aria-live="polite"
        sx={{
          minWidth: 22,
          textAlign: "center",
          fontFamily: FONT_BODY,
          fontWeight: 700,
          fontSize: 14,
          color: C.ink,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {quantity}
      </Box>
      <Box
        component="button"
        type="button"
        onClick={onAdd}
        disabled={disabled}
        aria-label={`Add one more ${itemName}`}
        sx={redAdd ? { ...stepButton, bgcolor: C.red, color: "#FFFFFF", "&:hover": { bgcolor: C.redHover } } : stepButton}
      >
        <AddRoundedIcon sx={{ fontSize: 18 }} />
      </Box>
    </Box>
  );
}

export default React.memo(QtyStepper);
